// src/components/Dashboard/SLASummaryCards.tsx
import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

interface SLASummaryCardsProps {
  teamData: any;
  userData: any;
}

const SLASummaryCards: React.FC<SLASummaryCardsProps> = ({ userData }) => {
  if (!userData || !userData.combined_compliance) return null;

  const allUsers = userData.combined_compliance.filter(
    (user: any) => user.total_processed > 0,
  );

  if (allUsers.length === 0) return null;

  // Calculate overall metrics from user data
  const totalProcessed = allUsers.reduce(
    (sum: number, user: any) => sum + user.total_processed,
    0,
  );
  const totalCompliant = allUsers.reduce(
    (sum: number, user: any) => sum + user.compliant,
    0,
  );
  const overallCompliance =
    totalProcessed > 0 ? (totalCompliant / totalProcessed) * 100 : 0;

  // Calculate before/after cutoff averages
  const beforeCutoffUsers =
    userData.before_cutoff_compliance?.filter(
      (user: any) => user.total_processed > 0,
    ) || [];
  const afterCutoffUsers =
    userData.after_cutoff_compliance?.filter(
      (user: any) => user.total_processed > 0,
    ) || [];

  const beforeTotalProcessed = beforeCutoffUsers.reduce(
    (sum: number, user: any) => sum + user.total_processed,
    0,
  );
  const beforeTotalCompliant = beforeCutoffUsers.reduce(
    (sum: number, user: any) => sum + user.compliant,
    0,
  );
  const beforeCutoffCompliance =
    beforeTotalProcessed > 0
      ? (beforeTotalCompliant / beforeTotalProcessed) * 100
      : 0;

  const afterTotalProcessed = afterCutoffUsers.reduce(
    (sum: number, user: any) => sum + user.total_processed,
    0,
  );
  const afterTotalCompliant = afterCutoffUsers.reduce(
    (sum: number, user: any) => sum + user.compliant,
    0,
  );
  const afterCutoffCompliance =
    afterTotalProcessed > 0
      ? (afterTotalCompliant / afterTotalProcessed) * 100
      : 0;

  // Color coding functions
  const getComplianceColor = (percentage: number) => {
    if (percentage >= 90) return "bg-green-100 text-green-600";
    if (percentage >= 80) return "bg-yellow-100 text-yellow-600";
    if (percentage >= 70) return "bg-orange-100 text-orange-600";
    return "bg-red-100 text-red-600";
  };

  // Card data configuration
  const cardItems = [
    {
      title: "Cumplimiento General SLA",
      value: `${overallCompliance.toFixed(1)}%`,
      icon: <CheckCircleOutlined className="text-2xl" />,
      color: getComplianceColor(overallCompliance),
      subtitle: `${totalCompliant.toLocaleString()}/${totalProcessed.toLocaleString()} requisitos`,
      target: null,
    },
    {
      title: "Primer Bloque",
      value: `${beforeCutoffCompliance.toFixed(1)}%`,
      icon: <ClockCircleOutlined className="text-2xl" />,
      color: getComplianceColor(beforeCutoffCompliance),
      subtitle: `${beforeTotalCompliant.toLocaleString()}/${beforeTotalProcessed.toLocaleString()} requisitos`,
      // target: "Meta: 85%",
      target: null,
    },
    {
      title: "Segundo Bloque",
      value: `${afterCutoffCompliance.toFixed(1)}%`,
      icon: <ExclamationCircleOutlined className="text-2xl" />,
      color: getComplianceColor(afterCutoffCompliance),
      subtitle: `${afterTotalCompliant.toLocaleString()}/${afterTotalProcessed.toLocaleString()} requisitos`,
      // target: "Meta: 90%",
      target: null,
    },
    {
      title: "Total Requisitos Procesados",
      value: totalProcessed.toLocaleString(),
      icon: <FileTextOutlined className="text-2xl" />,
      color: "bg-blue-100 text-blue-600",
      subtitle: `${allUsers.length} usuarios activos`,
      target: null,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {cardItems.map((item, index) => (
        <div key={index} className="bg-white rounded-lg shadow p-4">
          <div className="flex items-start">
            <div className={`rounded-md p-3 ${item.color}`}>{item.icon}</div>
            <div className="ml-4 flex-1">
              <p className="text-sm text-gray-500 mb-1">{item.title}</p>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {item.value}
              </p>
              {item.subtitle && (
                <p className="text-xs text-gray-500">{item.subtitle}</p>
              )}
              {item.target && (
                <p className="text-xs text-gray-400 mt-1">{item.target}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SLASummaryCards;
